<main class="p-4 p-xxl-5">
  <section class="row g-3 mb-4">
    <div v-for="stat in stats" :key="stat.label" class="col-12 col-md-6 col-xl-3">
      <article class="card border rounded-3 h-100">
        <div class="card-body p-4">
          <div class="text-uppercase text-muted fw-semibold mb-2">{{ stat.label }}</div>
          <div :class="['display-6 fw-bold mb-4', stat.tone]">{{ stat.value }}</div>
          <p class="mb-0 text-muted">{{ stat.detail }}</p>
        </div>
      </article>
    </div>
  </section>

  <section class="card border rounded-3 overflow-hidden">
    <div class="card-header bg-white d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4">
      <div class="d-inline-flex align-items-center flex-wrap border rounded-2 bg-light p-1" role="group" aria-label="Supplier onboarding filters">
        <button
          v-for="filter in journey_filters"
          :key="filter.label"
          type="button"
          @click="set_active_filter(filter.tone)"
          :class="[
            'btn btn-sm d-inline-flex align-items-center gap-2 px-3 py-1',
            active_filter === filter.tone ? 'bg-white border shadow-sm fw-semibold' : 'border-0 bg-transparent'
          ]"
        >
          <span
            v-if="filter.tone !== 'all'"
            class="rounded-circle d-inline-block onboarding-journey-dot"
            :style="journey_dot_style(filter.tone)"
          ></span>
          <span>{{ filter.label }}</span>
          <span>{{ journey_filter_count(filter) }}</span>
        </button>
      </div>

      <div class="d-flex align-items-center justify-content-end flex-wrap gap-2">
        <label class="onboarding-queue-search d-inline-flex align-items-center">
          <i class="bi bi-search" aria-hidden="true"></i>
          <input v-model="table_search" type="search" placeholder="Search table" @input="reset_table_page">
        </label>
        <button type="button" class="btn btn-sm bg-white border rounded-2 d-inline-flex align-items-center gap-2 fw-semibold px-3 py-1">
          <i class="bi bi-funnel"></i>
          <span>Filters</span>
        </button>
        <button type="button" class="btn btn-sm bg-white border rounded-2 d-inline-flex align-items-center gap-2 fw-semibold px-3 py-1">
          <i class="bi bi-download"></i>
          <span>Export</span>
        </button>
      </div>
    </div>

    <div class="table-responsive">
      <table class="table align-middle mb-0">
        <thead class="table-light">
          <tr>
            <th class="text-uppercase text-muted small fw-semibold py-3 ps-4">Onboarded Entity</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Journey</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Progress</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Risk</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Stage</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">Owner</th>
            <th class="text-uppercase text-muted small fw-semibold py-3">ETA</th>
            <th class="text-uppercase text-muted small fw-semibold py-3 pe-4">Activation</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filtered_supplier_cases.length === 0">
            <td colspan="8" class="text-center text-muted py-5">No supplier onboarding(s) for this journey.</td>
          </tr>
          <tr
            v-for="case_item in paginated_supplier_cases"
            :key="case_item.name"
            class="cursor-pointer"
            tabindex="0"
            @click="open_supplier_detail(case_item)"
            @keyup.enter="open_supplier_detail(case_item)"
          >
            <td class="ps-4">
              <div class="fw-bold text-black">{{ case_item.display_name }}</div>
              <div class="small text-muted mt-1">{{ case_item.detail }}</div>
            </td>
            <td>
              <span class="d-inline-flex align-items-center gap-2">
                <span class="rounded-circle d-inline-block onboarding-journey-dot" :style="journey_dot_style(case_item.journey_tone)"></span>
                <span>{{ case_item.journey }}</span>
              </span>
            </td>
            <td>
              <div class="d-flex align-items-center gap-3">
                <div class="progress flex-shrink-0 onboarding-progress-track-compact">
                  <div
                    class="progress-bar onboarding-progress-bar"
                    :style="{ '--onboarding-progress-size': `${case_item.progress}%`, '--onboarding-progress-color': progress_bar_color(case_item.progress_tone) }"
                  ></div>
                </div>
                <span class="small text-muted">{{ case_item.progress }}%</span>
              </div>
            </td>
            <td>
              <span :class="['badge rounded-pill fw-normal', risk_badge_class(case_item.risk_tone)]">
                {{ case_item.risk }}
              </span>
            </td>
            <td>{{ case_item.stage }}</td>
            <td>
              <span class="d-inline-flex align-items-center gap-2">
                <span class="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold text-white onboarding-list-initials-icon">
                  {{ case_item.owner_initials }}
                </span>
                <span>{{ case_item.owner }}</span>
              </span>
            </td>
            <td>{{ case_item.eta }}</td>
            <td class="pe-4">
              <span :class="['badge rounded-pill fw-normal', activation_badge_class(case_item.activation_tone)]">
                {{ case_item.activation }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <TablePagination
      :page="table_page"
      :page-size="table_page_size"
      :page-size-options="table_page_size_options"
      :total-rows="table_total_rows"
      :page-start="table_page_start"
      :page-end="table_page_end"
      :total-pages="table_total_pages"
      @update:page="set_table_page"
      @update:page-size="set_table_page_size"
    />
  </section>

  <div
    v-if="selected_supplier"
    class="onboarding-case-detail-backdrop"
    @click="close_supplier_detail"
  ></div>

  <aside
    v-if="selected_supplier"
    :class="['onboarding-case-detail-drawer bg-white shadow-lg', { 'is-fullscreen': is_detail_fullscreen }]"
  >
    <header class="border-bottom p-4">
      <div class="d-flex align-items-start justify-content-between gap-3">
        <div class="min-w-0">
          <div class="text-uppercase text-muted fw-semibold small mb-3">
            Supplier onboarding · {{ selected_supplier.stage }}
          </div>
          <h2 class="h5 fw-bold text-black mb-2">{{ selected_supplier.name }}</h2>
          <p class="mb-0 text-muted">Low-risk supplier · evidence complete</p>
        </div>

        <div class="d-flex align-items-center gap-3 flex-shrink-0">
          <span :class="['badge rounded-pill fw-normal', activation_badge_class(selected_supplier.activation_tone)]">
            <span class="me-1">●</span>{{ selected_supplier.activation }}
          </span>
          <button
            type="button"
            class="btn btn-link text-muted p-0"
            :aria-label="is_detail_fullscreen ? 'Exit fullscreen' : 'View fullscreen'"
            :title="is_detail_fullscreen ? 'Exit fullscreen' : 'View fullscreen'"
            @click="toggle_detail_fullscreen"
          >
            <i :class="['bi', is_detail_fullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen', 'fs-5']"></i>
          </button>
          <button type="button" class="btn btn-link text-muted p-0" aria-label="Close supplier detail" @click="close_supplier_detail">
            <i class="bi bi-x-lg fs-5"></i>
          </button>
        </div>
      </div>
    </header>

    <div class="onboarding-case-detail-body p-4">
      <nav class="bg-light rounded-3 p-1 d-flex align-items-center gap-2 mb-4" aria-label="Supplier detail sections">
        <button
          v-for="tab in detail_tabs"
          :key="tab.id"
          type="button"
          :class="[
            'btn btn-sm flex-fill d-inline-flex align-items-center justify-content-center gap-2',
            active_detail_tab === tab.id ? 'bg-white border shadow-sm fw-semibold' : 'border-0 bg-transparent'
          ]"
          @click="set_detail_tab(tab.id)"
        >
          <i :class="['bi', tab.icon]"></i>
          <span>{{ tab.label }}</span>
          <span v-if="tab.count" class="badge rounded-pill text-bg-danger">{{ tab.count }}</span>
        </button>
      </nav>

      <section v-if="active_detail_tab === 'summary'">
        <dl class="row g-0 mb-4">
          <template v-for="row in summary_rows" :key="row.label">
            <dt class="col-4 text-muted fw-normal mb-3">{{ row.label }}</dt>
            <dd class="col-8 fw-semibold mb-3">{{ row.value }}</dd>
          </template>
        </dl>

        <section class="mb-4">
          <div class="text-uppercase text-muted fw-semibold small mb-3">Overall readiness</div>
          <div class="d-flex align-items-center gap-3">
            <div class="progress flex-grow-1 onboarding-progress-track">
              <div class="progress-bar onboarding-progress-bar" :style="{ '--onboarding-progress-size': `${selected_supplier.progress}%` }"></div>
            </div>
            <span class="small fw-bold">{{ selected_supplier.progress }}%</span>
          </div>
        </section>

        <section>
          <div class="text-uppercase text-muted fw-semibold small mb-3">Stage timeline</div>
          <div class="onboarding-case-timeline">
            <article
              v-for="item in stage_timeline"
              :key="item.title"
              :class="['onboarding-case-timeline-item', `is-${item.status}`]"
            >
              <span class="onboarding-case-timeline-icon">
                <i :class="['bi', item.icon]"></i>
              </span>
              <div>
                <div class="fw-semibold text-black">{{ item.title }}</div>
                <div class="text-muted small mt-1">{{ item.detail }}</div>
              </div>
            </article>
          </div>
        </section>
      </section>
      <section v-else-if="active_detail_tab === 'profile'">
        <section class="mb-4">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <div class="text-uppercase text-muted fw-semibold small">Profile details</div>
            <span class="badge rounded-pill bg-info-subtle text-info-emphasis">
              <span class="me-1">&bull;</span>Applicant-submitted
            </span>
          </div>

          <dl class="row g-0 mb-0">
            <template v-for="row in profile_rows" :key="row.label">
              <dt class="col-4 text-muted fw-normal mb-3">{{ row.label }}</dt>
              <dd class="col-8 fw-semibold mb-3">{{ row.value }}</dd>
            </template>
          </dl>
        </section>

        <section v-if="contact_rows.length" class="mb-4">
          <div class="text-uppercase text-muted fw-semibold small mb-3">Key contacts</div>
          <div
            v-for="contact in contact_rows"
            :key="contact.name"
            class="d-flex align-items-start gap-3 py-2 border-bottom"
          >
            <span class="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold text-white flex-shrink-0 onboarding-initials-icon">
              {{ contact.initials }}
            </span>
            <div>
              <div class="fw-semibold text-black">{{ contact.name }}</div>
              <div class="text-muted small">{{ contact.detail }}</div>
            </div>
          </div>
        </section>
      </section>


      <section v-else-if="active_detail_tab === 'questionnaire'">
        <div class="d-flex align-items-center justify-content-between mb-4">
          <div class="text-uppercase text-muted fw-semibold small">Compliance questionnaire</div>
          <div class="text-uppercase text-muted fw-semibold small">{{ questionnaire_answers.length }} answer(s)</div>
        </div>

        <div>
          <article
            v-for="(answer, index) in questionnaire_answers"
            :key="answer.question"
            :class="['pb-4', index < questionnaire_answers.length - 1 ? 'mb-4 border-bottom border-light-subtle' : '']"
          >
            <p class="text-muted mb-2">{{ answer.question }}</p>
            <div class="d-flex align-items-center gap-2 fw-semibold text-black mb-2">
              <span :class="['rounded-circle d-inline-block onboarding-answer-dot-small', answer_dot_class(answer.tone)]"></span>
              <span>{{ answer.answer }}</span>
            </div>
            <div
              v-if="answer.note"
              :class="['rounded-2 px-3 py-2', answer.note_class]"
            >
              {{ answer.note }}
            </div>
          </article>
        </div>
      </section>
      <section v-else-if="active_detail_tab === 'agreements'">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div class="text-uppercase text-muted fw-semibold small">Documents &amp; agreements</div>
          <div class="text-uppercase text-muted fw-semibold small">{{ agreement_rows.length }} item(s)</div>
        </div>

        <div class="d-grid gap-3">
          <article
            v-for="agreement in agreement_rows"
            :key="agreement.id"
            class="border rounded-3 p-3"
          >
            <div class="d-flex align-items-start gap-3">
              <span :class="['rounded-3 d-inline-flex align-items-center justify-content-center flex-shrink-0 onboarding-icon-square-21', agreement.tone_class]">
                <i :class="['bi', agreement.icon]"></i>
              </span>
              <div class="min-w-0 flex-grow-1">
                <div class="fw-bold text-black mb-1">{{ agreement.title }}</div>
                <div class="text-muted small">{{ agreement.detail }}</div>
              </div>
              <div class="d-flex flex-column align-items-end gap-3 flex-shrink-0">
                <span :class="['badge rounded-pill', agreement.tone_class]">
                  <span class="me-1">&bull;</span>{{ agreement.status }}
                </span>
                <a
                  v-if="agreement.url"
                  :href="agreement.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="btn btn-link btn-sm text-muted text-decoration-none d-inline-flex align-items-center gap-2 p-0"
                >
                  <i class="bi bi-download"></i>
                  <span>Open</span>
                </a>
              </div>
            </div>
          </article>
        </div>
      </section>


      <section v-else class="text-center text-muted py-5">
        {{ detail_tabs.find((tab) => tab.id === active_detail_tab)?.label }} details will appear here.
      </section>
    </div>

    <footer class="onboarding-case-detail-footer border-top bg-white p-3 d-flex align-items-center justify-content-between gap-3">
      <button type="button" class="btn btn-link text-muted d-inline-flex align-items-center gap-2 text-decoration-none">
        <i class="bi bi-stars"></i>
        <span>Ask AI</span>
      </button>
      <div class="d-flex align-items-center gap-2">
        <button type="button" class="btn btn-outline-danger d-inline-flex align-items-center gap-2">
          <i class="bi bi-x-circle"></i>
          <span>Reject</span>
        </button>
        <button type="button" class="btn btn-outline-warning d-inline-flex align-items-center gap-2">
          <i class="bi bi-send"></i>
          <span>Request info</span>
        </button>
        <button type="button" class="btn btn-success d-inline-flex align-items-center gap-2">
          <i class="bi bi-play"></i>
          <span>Activate</span>
        </button>
      </div>
    </footer>
  </aside>

  <AgreementSignModal
    :visible="sign_modal.visible"
    :agreement-title="sign_modal.agreement_title"
    :signatory-name="sign_modal.signatory_name"
    :document-id="sign_modal.document_id"
    @close="close_sign_modal"
    @signed="complete_signature"
  />
</main>


